import { firstMissingField, reportSaveError, useFieldErrors } from './_shared';
import { friendlyDeleteError } from '../../utils/deleteError';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../Icon';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Select as FormSelect } from '../FormField';
import SearchableSelect from '../ui/SearchableSelect';
import RichTextEditor from '../ui/RichTextEditor';
import ConfirmDialog from '../ui/ConfirmDialog';
import { uploadImage, resolveMediaUrl } from '../../services/uploadService';
import { loadVendorData } from '../../data/VendorLoader';
import { StoredEvent } from '../../services/EventsService';
import { loadEvents, EVENTS_CHANGED_EVENT, EVENTS_LOADED_EVENT, eventsLoaded, refreshEventsFromApi } from '../../services/EventsService';
import { loadVenuesFromApi } from '../../services/VenueApiService';
import type { DecorCategory, DecorStatus } from '../../types/Decor';
import { DECOR_CATEGORY_ORDER, DECOR_CATEGORY_LABELS } from '../../types/Decor';
import {
  createDecorItem as svcCreateDecorItem,
  updateDecorItem as svcUpdateDecorItem,
  archiveDecorItem as svcArchiveDecorItem,
  restoreDecorItem as svcRestoreDecorItem,
  deleteDecorItem as svcDeleteDecorItem,
  refreshDecorFromApi,
  loadDecorAllocations,
  type DecorAllocationSource,
} from '../../services/DecorService';
import { useNotifications } from '../NotificationProvider';

const STORAGE_KEY_PROFILE = 'anojeje_couple_profile';
function addToPackingList(label: string) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
    if (!raw) return;
    const data = JSON.parse(raw);
    const item = { id: `pkg-${Date.now()}`, label, checked: false };
    if (data.bride) data.bride.packingList = [...(data.bride.packingList ?? []), item];
    localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(data));
  } catch { /* ignore */ }
}

interface DecorElementFormPageProps {
  decorId?: string | null;
  defaultCategory?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const DecorElementFormPage: React.FC<DecorElementFormPageProps> = ({
  decorId,
  defaultCategory,
  onSave,
  onCancel,
}) => {
  const location = useLocation();
  const fromBudget = (location.state as any)?.fromBudget as { vendor_name: string; budget_item_id: number; category_name?: string } | undefined;
  const fromProduct = (location.state as any)?.fromProduct as { product_name: string; product_id: number; supplier_name?: string } | undefined;

  const [form, setForm] = useState({
    category: (defaultCategory as DecorCategory) || 'FLORALS' as DecorCategory,
    area: '',
    decor_element: fromProduct?.product_name ?? '',
    color_scheme: '',
    quantity: '',
    vendor_name: fromBudget?.vendor_name ?? fromProduct?.supplier_name ?? '',
    notes: fromProduct ? `Procured item — see Procurement for order details` : '',
    status: (fromProduct ? 'procured' : 'planned') as DecorStatus,
    event_ids: [] as string[],
    venue_area: '',
    reference_images: [] as string[],
    canva_embeds: [] as string[],
  });

  const { showError, showSuccess, showInfo } = useNotifications();
  const fieldErrors = useFieldErrors();
  const [archived, setArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmPermanentDelete, setConfirmPermanentDelete] = useState(false);

  // Note: this form used to also track sourceBudgetItemId/sourceProductId
  // and write them onto the decor row as source_budget_item_id/
  // source_product_id. Both fields are deprecated (ANS-1) — the "Adding
  // procurement item…" banner below reads straight off `fromProduct`
  // instead, and sourcing provenance now lives entirely in
  // procurement_item_allocations.decor_item_id, resolved via the read-only
  // "Sourced from" panel in the sidebar.
  const [sourcedFrom, setSourcedFrom] = useState<DecorAllocationSource[]>([]);
  /** Units actually procured and on hand — read-only here (resolved from
   *  allocations server-side, see ANS-1); this form only reports it. */
  const [stock, setStock] = useState<number>(0);
  const [projectEvents, setProjectEvents] = useState<StoredEvent[]>([]);
  const [eventPickerOpen, setEventPickerOpen] = useState(false);
  const [packingToast, setPackingToast] = useState(false);
  const [decorVendorOptions, setDecorVendorOptions] = useState<{ value: string; label: string }[]>([]);
  const [venueOptions, setVenueOptions] = useState<{ value: string; label: string }[]>([]);
  const [venueRoomOptions, setVenueRoomOptions] = useState<{ value: string; label: string }[]>([]);

  const [newImageUrl, setNewImageUrl] = useState('');
  const [newCanvaUrl, setNewCanvaUrl] = useState('');
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const [photosUploading, setPhotosUploading] = useState(false);
  const decorTempIdRef = useRef<string>(crypto.randomUUID());

  const handlePhotoFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPhotosUploading(true);
    try {
      const uploads = await Promise.all(
        Array.from(files).map((file, i) =>
          uploadImage(file, {
            entityType: 'decor',
            entityId: decorId ?? decorTempIdRef.current,
            label: `reference_${i + 1}`,
            sortOrder: i,
          }),
        ),
      );
      const urls = uploads.map(a => a.url);
      setForm(prev => ({ ...prev, reference_images: [...prev.reference_images, ...urls] }));
    } catch (err) {
      console.error('Photo upload failed:', err);
    } finally {
      setPhotosUploading(false);
    }
  };

  useEffect(() => {
    const update = () => { try { setProjectEvents(loadEvents()); } catch { /* ignore */ } };
    update();
    if (!eventsLoaded()) void refreshEventsFromApi();
    window.addEventListener(EVENTS_CHANGED_EVENT, update);
    window.addEventListener(EVENTS_LOADED_EVENT, update);
    return () => {
      window.removeEventListener(EVENTS_CHANGED_EVENT, update);
      window.removeEventListener(EVENTS_LOADED_EVENT, update);
    };
  }, []);

  useEffect(() => {
    loadVendorData()
      .then(vs => setDecorVendorOptions([
        { value: '', label: 'None (optional)' },
        ...vs.map(v => ({ value: v.name, label: v.name })),
      ]))
      .catch(() => {});
  }, []);

  // Read-only provenance — replaces the old standalone Procurement Order
  // dropdown (see ANS-1). Resolved from procurement_item_allocations rows
  // linked via decor_item_id, not from a field on the decor row itself, so
  // it only has anything to show once the item is saved.
  useEffect(() => {
    if (!decorId) { setSourcedFrom([]); return; }
    let cancelled = false;
    void loadDecorAllocations(decorId).then(rows => { if (!cancelled) setSourcedFrom(rows); }).catch(() => {});
    return () => { cancelled = true; };
  }, [decorId]);

  useEffect(() => {
    void loadVenuesFromApi().then(venues => {
      const venueNames = venues.filter(v => !v.archived).map(v => v.name);
      const eventVenues = projectEvents
        .map(e => e.venue)
        .filter((v): v is string => typeof v === 'string' && v.length > 0 && !venueNames.includes(v));
      const all = [...venueNames, ...eventVenues];
      setVenueOptions([
        { value: '', label: 'Select venue / area…' },
        ...all.map(v => ({ value: v, label: v })),
      ]);
      // Pre-fill area from linked budget vendor name if it matches a venue
      if (fromBudget && !form.area) {
        const match = all.find(v => v.toLowerCase() === fromBudget.vendor_name.toLowerCase())
          ?? all.find(v => fromBudget.vendor_name.toLowerCase().includes(v.toLowerCase()))
          ?? all.find(v => v.toLowerCase().includes(fromBudget.vendor_name.toLowerCase()));
        if (match) setForm(prev => ({ ...prev, area: match }));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectEvents]);

  useEffect(() => {
    void loadVenuesFromApi().then(venues => {
      const matched = venues.find(v => !v.archived && v.name === form.area);
      if (matched && matched.floorPlans.length > 0) {
        const rooms = matched.floorPlans.flatMap(fp => fp.rooms);
        setVenueRoomOptions([
          { value: '', label: 'Select spot…' },
          ...rooms.map(r => ({ value: r.name, label: r.name })),
        ]);
      } else {
        setVenueRoomOptions([]);
      }
    }).catch(() => {});
  }, [form.area]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('decor-element-status-changed', { detail: form.status }));
  }, [form.status]);

  useEffect(() => {
    const handler = (e: Event) => {
      const status = (e as CustomEvent<string>).detail as DecorStatus;
      // The old guard here ("procured requires procurementOrderId or
      // sourceProductId") gated on the two fields ANS-1 retires — sourcing
      // is now tracked via allocations, resolved after save, so it can no
      // longer be checked synchronously at status-change time. Status is a
      // plain manual field now; whether real stock backs it is visible in
      // the read-only "Sourced from" panel instead.
      setForm(prev => ({ ...prev, status }));
    };
    window.addEventListener('decor-element-set-status', handler);
    return () => window.removeEventListener('decor-element-set-status', handler);
  }, []);

  useEffect(() => {
    if (!decorId) return;
    void refreshDecorFromApi().then(items => {
      const item = items.find(i => i.id === decorId);
      if (!item) return;
      setForm({
        category: item.category,
        area: item.area,
        decor_element: item.decor_element,
        color_scheme: item.color_scheme,
        quantity: item.quantity != null ? String(item.quantity) : '',
        vendor_name: item.vendor_name,
        notes: item.notes,
        status: item.status,
        event_ids: item.event_ids ?? [],
        venue_area: item.venue_area ?? '',
        reference_images: item.reference_images ?? [],
        canva_embeds: item.canva_embeds ?? [],
      });
      setStock(item.stock ?? 0);
      setArchived(!!item.archived);
    });
  }, [decorId]);

  const set = (field: string, value: string) => {
    fieldErrors.clear(field);
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addImage = () => {
    const url = newImageUrl.trim();
    if (!url || form.reference_images.includes(url)) return;
    setForm(prev => ({ ...prev, reference_images: [...prev.reference_images, url] }));
    setNewImageUrl('');
  };

  const removeImage = (src: string) =>
    setForm(prev => {
      const idx = prev.reference_images.indexOf(src);
      if (idx === -1) return prev;
      const next = [...prev.reference_images];
      next.splice(idx, 1);
      return { ...prev, reference_images: next };
    });

  const addCanva = () => {
    const url = newCanvaUrl.trim();
    if (!url || form.canva_embeds.includes(url)) return;
    setForm(prev => ({ ...prev, canva_embeds: [...prev.canva_embeds, url] }));
    setNewCanvaUrl('');
  };

  const removeCanva = (url: string) =>
    setForm(prev => ({ ...prev, canva_embeds: prev.canva_embeds.filter(u => u !== url) }));

  const handleAddToPackingList = () => {
    const label = form.area || form.decor_element || 'Decor item';
    addToPackingList(label);
    setPackingToast(true);
    setTimeout(() => setPackingToast(false), 3000);
  };

  const handleSave = useCallback(async () => {
    const missing = firstMissingField([{ label: 'Area', value: form.area }]);
    if (missing) { showError(missing); return; }
    fieldErrors.clear();
    // The old "procured requires a linked procurement order" guard checked
    // procurement_order_id/source_product_id — both deprecated (ANS-1).
    // Sourcing now lives in allocations, resolved after save; nothing here
    // can gate on it synchronously any more.
    const data = {
      category: form.category,
      area: form.area.trim(),
      decor_element: form.decor_element.trim(),
      color_scheme: form.color_scheme.trim(),
      quantity: form.quantity !== '' ? Number(form.quantity) : null,
      vendor_name: form.vendor_name.trim(),
      notes: form.notes.trim(),
      status: form.status,
      event_ids: form.event_ids,
      venue_area: form.venue_area.trim(),
      reference_images: form.reference_images,
      canva_embeds: form.canva_embeds,
    };
    try {
      if (decorId) {
        const updated = await svcUpdateDecorItem(decorId, data);
        if (!updated) { showError('That decor item is no longer listed — refresh and try again.'); return; }
      } else {
        await svcCreateDecorItem(data);
      }
    } catch (err) {
      // Don't call onSave — it navigates away, which used to strand the user on
      // the décor list believing an item had been saved that never was.
      reportSaveError(err, fieldErrors, showError, 'Failed to save decor item.');
      return;
    }
    onSave();
  }, [form, decorId, onSave, showError]);

  const handleDelete = useCallback(() => {
    if (!decorId) return;
    setConfirmArchive(true);
  }, [decorId]);

  const handleConfirmArchive = useCallback(async () => {
    if (!decorId) return;
    setConfirmArchive(false);
    try {
      await svcArchiveDecorItem(decorId);
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'decor item', action: 'archive' }));
      return;
    }
    showInfo(`"${form.decor_element || form.area}" archived — not permanently deleted. You can restore it from the Décor list.`, { persistent: true });
    onSave();
  }, [decorId, form.decor_element, form.area, onSave, showInfo, showError]);

  const handleRestore = useCallback(async () => {
    if (!decorId) return;
    try {
      await svcRestoreDecorItem(decorId);
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'decor item', action: 'restore' }));
      return;
    }
    showSuccess(`"${form.decor_element || form.area}" restored`);
    setArchived(false);
  }, [decorId, form.decor_element, form.area, showSuccess, showError]);

  const handleConfirmPermanentDelete = useCallback(async () => {
    if (!decorId) return;
    setConfirmPermanentDelete(false);
    try {
      await svcDeleteDecorItem(decorId);
    } catch (err) {
      // The archive-first refusal reaches the user here instead of being
      // dropped as a bare `false` the caller never read.
      showError(friendlyDeleteError(err, { record: 'decor item', action: 'delete' }));
      return;
    }
    showSuccess(`"${form.decor_element || form.area}" permanently deleted`, { persistent: true });
    onSave();
  }, [decorId, form.decor_element, form.area, onSave, showSuccess, showError]);

  useEffect(() => {
    window.addEventListener('decor-element-save', handleSave);
    window.addEventListener('decor-element-cancel', onCancel);
    window.addEventListener('decor-element-delete', handleDelete);
    return () => {
      window.removeEventListener('decor-element-save', handleSave);
      window.removeEventListener('decor-element-cancel', onCancel);
      window.removeEventListener('decor-element-delete', handleDelete);
    };
  }, [handleSave, onCancel, handleDelete]);

  return (
    <div className="detail-layout decor-detail-layout">
      <div className="inline-form-page">
        {archived && (
          <div className="ds-badge ds-badge-neutral cf-archived-banner">
            <span>Archived — restore it below to edit further, or delete it permanently</span>
            <button type="button" className="header-action-btn" onClick={handleRestore}>Restore</button>
            <button type="button" className="header-action-btn header-btn-danger" onClick={() => setConfirmPermanentDelete(true)}>
              <Icon name="trash" size={14} /> Delete permanently
            </button>
          </div>
        )}
        {fromProduct && (
          <div className="form-info-banner">
            <Icon name="link" size={13} /> Adding procurement item: <strong>{fromProduct.product_name}</strong> to Décor
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Events</label>
          <div className={`tag-multiselect${eventPickerOpen ? ' open' : ''}`}>
            <div className="tag-multiselect-trigger" onClick={() => setEventPickerOpen(o => !o)}>
              <div className="tag-multiselect-chips">
                {form.event_ids.length === 0
                  ? <span className="tag-multiselect-placeholder">All events</span>
                  : form.event_ids.map(eid => {
                      const ev = projectEvents.find(e => e.id === eid);
                      return (
                        <span key={eid} className="tms-chip">
                          <span className="tms-chip-label">{ev?.title ?? eid}</span>
                          <button type="button" className="tms-chip-x" onClick={e => { e.stopPropagation(); setForm(prev => ({ ...prev, event_ids: prev.event_ids.filter(id => id !== eid) })); }}>×</button>
                        </span>
                      );
                    })
                }
              </div>
              <span className="tag-multiselect-caret">▾</span>
            </div>
            {eventPickerOpen && (
              <div className="tag-multiselect-menu">
                {projectEvents.map(ev => {
                  const selected = form.event_ids.includes(ev.id);
                  return (
                    <div key={ev.id} className={`tag-multiselect-option${selected ? ' selected' : ''}`}
                      onClick={() => setForm(prev => ({ ...prev, event_ids: selected ? prev.event_ids.filter(id => id !== ev.id) : [...prev.event_ids, ev.id] }))}>
                      <span className="tag-multiselect-check">{selected ? '✓' : ''}</span>
                      {ev.title}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <p className="form-section-title">Location & Details</p>
          <div className="form-col-2">
            <FormSelect label="Area / Venue" value={form.area} onChange={e => set('area', e.target.value)} options={venueOptions} required />
            {venueRoomOptions.length > 0 ? (
              <FormSelect label="Venue Spot" value={form.venue_area} onChange={e => set('venue_area', e.target.value)} options={venueRoomOptions} />
            ) : (
              <FormInput label="Venue Spot" value={form.venue_area} onChange={e => set('venue_area', e.target.value)} placeholder="e.g. Entrance Arch, Main Stage" />
            )}
            <FormInput label="Element / Description" error={fieldErrors.errorFor('decor_element')} value={form.decor_element} onChange={e => set('decor_element', e.target.value)} placeholder="e.g. Flower petals" />
            <FormInput label="Color Scheme" error={fieldErrors.errorFor('color_scheme')} value={form.color_scheme} onChange={e => set('color_scheme', e.target.value)} placeholder="e.g. Gold, Ivory" />
            <FormInput label="Quantity" type="number" min="0" error={fieldErrors.errorFor('quantity')} value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="—" />
          </div>

          <RichTextEditor label="Notes" rows={4} value={form.notes} onChange={val => set('notes', val)} placeholder="Any special instructions or details…" />
        </div>

        <div className="form-section">
          <p className="form-section-title">Moodboards</p>
          <div className="form-group">
            <label className="form-label">Canva Moodboards</label>
            <p className="dp-field-hint">Paste a Canva share link — set to "Anyone with link can view" first.</p>
            {form.canva_embeds.map(url => (
              <div key={url} className="dp-canva-embed-wrap">
                <div className="dp-canva-embed-header">
                  <span className="dp-canva-embed-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    {url.length > 60 ? url.slice(0, 57) + '…' : url}
                  </span>
                  <button type="button" className="dp-canva-remove" onClick={() => removeCanva(url)} aria-label="Remove moodboard">×</button>
                </div>
                <iframe
                  src={url.includes('/embed') ? url : url.replace(/\/(view|edit).*$/, '/view?embed')}
                  className="dp-canva-iframe"
                  allowFullScreen
                  allow="fullscreen"
                  title="Canva moodboard"
                />
              </div>
            ))}
            <div className="dp-url-add-row">
              <input className="form-input" value={newCanvaUrl} onChange={e => setNewCanvaUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCanva(); } }}
                placeholder="https://www.canva.com/design/…/view" />
              <button type="button" className="dp-add-url-btn" onClick={addCanva}>Add</button>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-sidebar">
        <section className="side-section">
          <h4>Category</h4>
          <FormSelect
            label=""
            value={form.category}
            onChange={e => set('category', e.target.value)}
            options={DECOR_CATEGORY_ORDER.map(c => ({ value: c, label: DECOR_CATEGORY_LABELS[c] }))}
          />
        </section>

        <section className="side-section">
          <h4>Vendor</h4>
          <SearchableSelect
            value={form.vendor_name}
            onChange={value => set('vendor_name', value)}
            options={decorVendorOptions.length > 0 ? decorVendorOptions.filter(o => o.value !== '') : []}
            placeholder={decorVendorOptions.length > 0 ? 'Search vendors or type a name…' : 'Loading…'}
          />
        </section>

        {/* Read-only — replaces the old standalone Procurement Order dropdown
            (see ANS-1). Sourcing is now set from the Allocation panel on the
            procurement order itself (link an existing decor item, or leave
            it unset to auto-create one on delivery); this form only reports
            what actually landed. Only meaningful once the item is saved —
            allocations link to a real decor_item_id. */}
        <section className="side-section">
          <h4>Sourced From</h4>
          <div className="stat-row"><span>Stock on hand</span><strong>{stock}</strong></div>
          {!decorId ? (
            <p className="dp-field-hint">Save this item, then link it from a procurement order's Allocation panel.</p>
          ) : sourcedFrom.length === 0 ? (
            <p className="dp-field-hint">Not yet sourced from any procurement order.</p>
          ) : (
            sourcedFrom.map(row => (
              <div className="stat-row" key={row.allocation_id}>
                <span>
                  {row.marketplace_order_id ? `PO ${row.marketplace_order_id}` : `Order #${row.order_id}`}
                  {row.vendor_name ? ` — ${row.vendor_name}` : ''}
                </span>
                <strong>{row.quantity} units</strong>
              </div>
            ))
          )}
        </section>

        <section className="side-section">
          <h4>Inspiration Photos</h4>
          {form.reference_images.length > 0 && (
            <div className="dp-gallery dp-gallery--sidebar">
              {form.reference_images.map((src, i) => (
                <div key={i} className="dp-gallery-thumb">
                  <img src={resolveMediaUrl(src)}
                    alt={`Inspiration ${i + 1}`} className="dp-gallery-img"
                    onError={e => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27100%27 height=%27100%27%3E%3Crect fill=%27%23f3f4f6%27 width=%27100%27 height=%27100%27/%3E%3C/svg%3E'; }} />
                  <button type="button" className="dp-gallery-remove" onClick={() => removeImage(src)} aria-label="Remove">×</button>
                </div>
              ))}
            </div>
          )}
          <label
            className={`sidebar-photo-upload${form.reference_images.length > 0 ? ' sidebar-photo-upload--mt' : ''}`}
            onClick={e => { e.preventDefault(); photoFileInputRef.current?.click(); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handlePhotoFiles(e.dataTransfer.files); }}
          >
            <span className="sidebar-photo-upload-btn">
              {photosUploading ? 'Uploading…' : '+ Add Photo'}
            </span>
          </label>
          <input ref={photoFileInputRef} type="file" accept="image/*" multiple className="u-hidden"
            onChange={e => handlePhotoFiles(e.target.files)} />
          <div className="dp-url-add-row">
            <input className="form-input" value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addImage(); } }}
              placeholder="Or paste image URL…" />
            <button type="button" className="dp-add-url-btn" onClick={addImage}>Add</button>
          </div>
        </section>
      </div>
      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={handleConfirmArchive}
        title="Archive this item?"
        description="It will be hidden from the active list but not permanently deleted. You can restore it later from the Archived filter."
        confirmLabel="Archive"
      />
      <ConfirmDialog
        open={confirmPermanentDelete}
        onClose={() => setConfirmPermanentDelete(false)}
        onConfirm={handleConfirmPermanentDelete}
        title="Permanently delete this item?"
        description="This cannot be undone."
        confirmLabel="Delete"
      />
    </div>
  );
};
